import React, { useEffect, useState } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { useAuth } from '@/components/Auth/AuthContext';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import CompanyAiNavigationTabs from './CompanyAiNavigationTabs';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Input,
  PageSection,
  Select,
  StatusBadge,
  Textarea,
} from '@/ui/components';
import {
  CompanyEmailReviewDetail,
  CompanyEmailReviewListItem,
  CompanyEmailReviewStatus,
  companyAiService,
} from '@/services/companyAiService';
import { getVisibleProjectSuggestions } from './emailReviewProjectSuggestions';
import { EmailClassificationBadge, EmailReviewStatusBadge, formatEmailClassification } from './emailReviewPresentation';
import './companyAi.css';
import EmailHistoryPanel from '@/components/EmailHistory/EmailHistoryPanel';
import {emailHistoryService} from '@/services/emailHistoryService';

type EmailTaskCreateDraft = {
  title: string;
  description: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high' | '';
};

const EMAIL_REVIEW_TIMEZONE = 'Australia/Melbourne';

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

function formatDateTime(value?: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: EMAIL_REVIEW_TIMEZONE,
  });
}

function formatReasonCode(value: string) {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatReasonCodeList(values: string[]) {
  return values.map((value) => formatReasonCode(value)).join(', ');
}

function formatEvidenceSummary(evidence: Array<{ source: string; text: string }>) {
  return evidence
    .map((item) => String(item.text || '').trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(' · ');
}

function formatGraphEvidenceDetail(evidence: NonNullable<CompanyEmailReviewDetail['projectSuggestions'][number]['graphEvidence']>[number]) {
  const percent = Math.round((Number(evidence.confidence) || 0) * 100);
  return `${evidence.relationPath.replace(/->/g, ' -> ')} · ${percent}%${evidence.sourceDisplayName ? ` · ${evidence.sourceDisplayName}` : ''}`;
}

function buildEmailTaskCreateDraft(detail: CompanyEmailReviewDetail, suggestionId: number): EmailTaskCreateDraft | null {
  const suggestion = detail.taskSuggestions.find((item) => item.emailTaskSuggestionId === suggestionId);
  if (!suggestion) return null;
  return {
    title: suggestion.suggestedTitle || '',
    description: suggestion.suggestedDescription || suggestion.suggestedTitle || '',
    dueDate: suggestion.suggestedDueDate || '',
    priority: (suggestion.suggestedPriority || '') as EmailTaskCreateDraft['priority'],
  };
}

export default function AiEmailPage() {
  const { user } = useAuth();
  const [emailMode,setEmailMode]=useState<'suggestions'|'history'>('suggestions');
  const [historyResultCount,setHistoryResultCount]=useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [emailReviewList, setEmailReviewList] = useState<CompanyEmailReviewListItem[]>([]);
  const [emailReviewTotalCount, setEmailReviewTotalCount] = useState(0);
  const [selectedEmailReviewId, setSelectedEmailReviewId] = useState<number | null>(null);
  const [selectedEmailReviewDetail, setSelectedEmailReviewDetail] = useState<CompanyEmailReviewDetail | null>(null);
  const [emailReviewLoading, setEmailReviewLoading] = useState(false);
  const [emailReviewFeedback, setEmailReviewFeedback] = useState<string | null>(null);
  const [emailReviewFeedbackTone, setEmailReviewFeedbackTone] = useState<'error' | 'success'>('success');
  const [emailReviewClassification, setEmailReviewClassification] = useState('');
  const [emailReviewStatusFilter, setEmailReviewStatusFilter] = useState('');
  const [openTaskCreateSuggestionId, setOpenTaskCreateSuggestionId] = useState<number | null>(null);
  const [emailTaskCreateDrafts, setEmailTaskCreateDrafts] = useState<Record<number, EmailTaskCreateDraft>>({});

  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const visibleProjectSuggestions = getVisibleProjectSuggestions(selectedEmailReviewDetail);

  async function loadEmailReviews(options?: { showPageLoading?: boolean }) {
    if (options?.showPageLoading) {
      setPageLoading(true);
    }

    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);

    try {
      const result = await companyAiService.listEmailReviewItems({
        page: 1,
        pageSize: 20,
        classification: emailReviewClassification || undefined,
        reviewStatus: emailReviewStatusFilter || undefined,
      });

      setEmailReviewList(result.items);
      setEmailReviewTotalCount(result.totalCount);

      if (selectedEmailReviewId && !result.items.some((item) => item.emailMessageId === selectedEmailReviewId)) {
        setSelectedEmailReviewId(null);
        setSelectedEmailReviewDetail(null);
        setOpenTaskCreateSuggestionId(null);
      }
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
      if (options?.showPageLoading) {
        setPageLoading(false);
      }
    }
  }

  useEffect(() => {
    void loadEmailReviews({ showPageLoading: true });
  }, [emailReviewClassification, emailReviewStatusFilter]);

  async function reloadSelectedEmailReview(emailMessageId: number) {
    setSelectedEmailReviewDetail(await companyAiService.getEmailReviewItem(emailMessageId));
  }

  async function handleSelectEmailReview(emailMessageId: number) {
    setSelectedEmailReviewId(emailMessageId);
    setOpenTaskCreateSuggestionId(null);
    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);

    try {
      await reloadSelectedEmailReview(emailMessageId);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function handleReviewEmailMessage(reviewStatus: CompanyEmailReviewStatus) {
    if (!selectedEmailReviewId) return;

    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);

    try {
      await companyAiService.reviewEmailMessage({
        emailMessageId: selectedEmailReviewId,
        reviewStatus,
      });
      setEmailReviewFeedback(
        reviewStatus === 'accepted'
          ? `Email ${selectedEmailReviewId} kept for PM follow-up review.`
          : `Email ${selectedEmailReviewId} ignored for PM follow-up review.`
      );
      setEmailReviewFeedbackTone('success');
      await loadEmailReviews();
      await reloadSelectedEmailReview(selectedEmailReviewId);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function handleReviewSuggestion(
    kind: 'project' | 'task' | 'file',
    suggestionId: number,
    reviewStatus: CompanyEmailReviewStatus
  ) {
    if (!selectedEmailReviewId) return;

    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);

    try {
      await companyAiService.reviewEmailSuggestion({
        kind,
        suggestionId,
        reviewStatus,
      });
      setEmailReviewFeedback(
        kind === 'project' && reviewStatus === 'accepted'
          ? `Project suggestion ${suggestionId} kept. Other project matches for this email were ignored.`
          : `${kind} suggestion ${suggestionId} ${reviewStatus === 'accepted' ? 'kept' : 'dismissed'}.`
      );
      setEmailReviewFeedbackTone('success');
      await reloadSelectedEmailReview(selectedEmailReviewId);
      await loadEmailReviews();
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function handleReprocessEmailMessage() {
    if (!selectedEmailReviewId) return;

    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    setOpenTaskCreateSuggestionId(null);

    try {
      const result = await companyAiService.reprocessEmailMessage(selectedEmailReviewId);
      setEmailReviewFeedback(
        `Reprocessed email ${selectedEmailReviewId}. Rebuilt ${result.projectSuggestionCount} project, ${result.taskSuggestionCount} task, and ${result.fileSuggestionCount} attachment suggestions. Suggestion-level keep or dismiss choices were reset for the rebuilt rows.`
      );
      setEmailReviewFeedbackTone('success');
      await loadEmailReviews();
      await reloadSelectedEmailReview(selectedEmailReviewId);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  function handleOpenTaskCreateForm(suggestionId: number) {
    if (!selectedEmailReviewDetail) return;

    setOpenTaskCreateSuggestionId(suggestionId);
    setEmailReviewFeedback(null);
    setEmailTaskCreateDrafts((current) => {
      if (current[suggestionId]) return current;
      const draft = buildEmailTaskCreateDraft(selectedEmailReviewDetail, suggestionId);
      if (!draft) return current;
      return {
        ...current,
        [suggestionId]: draft,
      };
    });
  }

  function handleTaskCreateDraftChange(
    suggestionId: number,
    field: keyof EmailTaskCreateDraft,
    value: string
  ) {
    setEmailTaskCreateDrafts((current) => ({
      ...current,
      [suggestionId]: {
        ...(current[suggestionId] || { title: '', description: '', dueDate: '', priority: '' }),
        [field]: value,
      },
    }));
  }

  async function handleCreateTaskFromSuggestion(suggestionId: number) {
    if (!selectedEmailReviewId) return;

    const draft = emailTaskCreateDrafts[suggestionId];
    if (!draft) return;

    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);

    try {
      const result = await companyAiService.createTaskFromEmailSuggestion({
        suggestionId,
        title: draft.title,
        description: draft.description,
        dueDate: draft.dueDate || null,
        priority: (draft.priority || null) as 'low' | 'medium' | 'high' | null,
      });

      setEmailReviewFeedback(
        result.alreadyCreated
          ? `Task ${result.createdTaskId} was already linked to suggestion ${suggestionId}.`
          : `Created PM task ${result.createdTaskId} from email suggestion ${suggestionId}.`
      );
      setEmailReviewFeedbackTone('success');
      await loadEmailReviews();
      await reloadSelectedEmailReview(selectedEmailReviewId);
      setOpenTaskCreateSuggestionId(null);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  return (
    <div className="company-ai-shell">
      <PageHeader
        title={emailMode==='history'?'Email history':'Email Suggestions'}
        subtitle={emailMode==='history'?'Find imported correspondence and inspect its verified reply or conversation links.':'Review suggestion-only email classifications, project matches, task suggestions, and attachment routing hints.'}
        itemCount={emailMode==='history'?historyResultCount:emailReviewTotalCount}
        gradientColor="linear-gradient(135deg, #22314d 0%, #3558a8 52%, #7f94c6 100%)"
        icon="📬"
        searchEnabled={false}
        sortEnabled={false}
        userLabel={user?.userDetails || 'Unknown'}
        roleLabel={roleLabel}
      />

      <CompanyAiNavigationTabs
        currentPage="email"
        activeWorkspaceTab="ask-ai"
        userEmail={user?.userDetails}
        userRoles={user?.userRoles}
      />

      <PageSection width="wide" padding="compact" className="company-ai-admin-section company-ai-admin-section--main">
        <div className="email-history-modes" role="group" aria-label="Email view">
          <Button variant={emailMode==='suggestions'?'primary':'secondary'} aria-pressed={emailMode==='suggestions'} onClick={()=>setEmailMode('suggestions')}>Suggestions</Button>
          <Button variant={emailMode==='history'?'primary':'secondary'} aria-pressed={emailMode==='history'} onClick={()=>setEmailMode('history')}>Search history</Button>
        </div>
        {emailMode==='history' ? <Card><EmailHistoryPanel key={user?.userDetails || 'mailbox'} client={emailHistoryService} onResultCount={setHistoryResultCount} /></Card> : <>
        <Card
          header={(
            <CardHeader
              title="Email Suggestion Review"
              subtitle="Review the latest ingested emails, approve the useful suggestions, and turn accepted task suggestions into real PM work when needed."
              actions={<StatusBadge status={emailReviewList.length ? 'active' : 'warning'}>{emailReviewTotalCount} emails</StatusBadge>}
            />
          )}
        >
          <div className="company-ai-stacked">
            <div className="company-ai-form-grid company-ai-form-grid--quota-compact">
              <Select
                label="Classification"
                value={emailReviewClassification}
                onChange={(event) => setEmailReviewClassification(event.target.value)}
                options={[
                  { value: '', label: 'All classes' },
                  { value: 'quote_inquiry', label: 'Quote inquiry' },
                  { value: 'project_update', label: 'Project update' },
                  { value: 'invoice_payment', label: 'Invoice/payment' },
                  { value: 'rfi_question', label: 'RFI/question' },
                  { value: 'document_submission', label: 'Document submission' },
                  { value: 'site_issue', label: 'Site issue' },
                  { value: 'general_admin', label: 'General admin' },
                  { value: 'low_value_noise', label: 'Low-value/noise' },
                ]}
                fullWidth
              />
              <Select
                label="Review status"
                value={emailReviewStatusFilter}
                onChange={(event) => setEmailReviewStatusFilter(event.target.value)}
                options={[
                  { value: '', label: 'All review states' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'rejected', label: 'Rejected' },
                ]}
                fullWidth
              />
            </div>

            <div className="company-ai-muted">All dates and times on this page are shown in Melbourne time.</div>

            {emailReviewFeedback ? (
              <div className={`company-ai-feedback company-ai-feedback--${emailReviewFeedbackTone}`}>
                {emailReviewFeedback}
              </div>
            ) : null}

            <div className="company-ai-email-review-layout">
              <div className="company-ai-primary">
                {pageLoading ? (
                  <div className="company-ai-muted">Loading email suggestions...</div>
                ) : emailReviewList.length ? (
                  <div className="company-ai-list company-ai-list--inventory">
                    {emailReviewList.map((item) => (
                      <button
                        key={item.emailMessageId}
                        type="button"
                        className={`company-ai-list-button${selectedEmailReviewId === item.emailMessageId ? ' company-ai-list-button--active' : ''}`}
                        onClick={() => void handleSelectEmailReview(item.emailMessageId)}
                      >
                        <div className="company-ai-list-title">{item.subject || '(no subject)'}</div>
                        <div className="company-ai-list-subtitle">
                          {item.fromEmail || 'unknown sender'}
                          {' · '}
                          <EmailClassificationBadge value={item.classification} />
                          {' '}
                          {formatDateTime(item.receivedAt)}
                        </div>
                        <div className="company-ai-muted company-ai-muted--spaced">
                          {item.bodyPreview || 'No preview available'}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="📬"
                    title="No email suggestions yet"
                    description="Ingest emails through the webhook or mailbox bridge and the review queue will appear here."
                    className="company-ai-admin-empty-state"
                  />
                )}
              </div>

              <div className="company-ai-sidebar">
                {selectedEmailReviewDetail ? (
                  <>
                    <div className="company-ai-result-box company-ai-email-review-detail">
                      <div className="company-ai-section-title company-ai-section-title--flush">{selectedEmailReviewDetail.message.subject}</div>
                      <div className="company-ai-email-toolbar">
                        <div className="company-ai-email-meta-grid">
                          <div className="company-ai-email-meta">
                            <span className="company-ai-email-meta-label">From</span>
                            <span className="company-ai-email-meta-value">{selectedEmailReviewDetail.message.fromEmail || 'unknown'}</span>
                          </div>
                          <div className="company-ai-email-meta">
                            <span className="company-ai-email-meta-label">Received</span>
                            <span className="company-ai-email-meta-value">{formatDateTime(selectedEmailReviewDetail.message.receivedAt)}</span>
                          </div>
                          <div className="company-ai-email-meta">
                            <span className="company-ai-email-meta-label">Classification</span>
                            <span className="company-ai-email-meta-value">
                              <EmailClassificationBadge value={selectedEmailReviewDetail.message.classification} />
                            </span>
                          </div>
                          <div className="company-ai-email-meta">
                            <span className="company-ai-email-meta-label">Review status</span>
                            <span className="company-ai-email-meta-value">
                              <EmailReviewStatusBadge value={selectedEmailReviewDetail.message.reviewStatus} />
                            </span>
                          </div>
                        </div>
                        <div className="company-ai-form-actions company-ai-form-actions--email-review">
                          <Button loading={emailReviewLoading} onClick={() => void handleReviewEmailMessage('accepted')}>Keep For PM Follow-up</Button>
                          <Button variant="secondary" loading={emailReviewLoading} onClick={() => void handleReviewEmailMessage('rejected')}>Ignore For PM Follow-up</Button>
                          <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReprocessEmailMessage()}>Reprocess Suggestions</Button>
                        </div>
                      </div>
                      <div className="company-ai-message-caption">
                        {formatEmailClassification(selectedEmailReviewDetail.message.classification)} emails are classified from subject and body. Keep For PM Follow-up marks this email as worth acting on inside PMapp.
                      </div>
                      <div className="company-ai-message-caption">
                        Melbourne time: {formatDateTime(selectedEmailReviewDetail.message.receivedAt)}
                      </div>
                      <div className="company-ai-message-body company-ai-message-body--email-preview">
                        {selectedEmailReviewDetail.message.bodyPreview || selectedEmailReviewDetail.message.normalizedBody || 'No body preview available'}
                      </div>
                    </div>

                    <aside className="company-ai-email-review-suggestions" aria-label="Email suggestions">
                      <div className="company-ai-result-box company-ai-result-box--compact">
                      <div className="company-ai-section-title company-ai-section-title--flush">Project suggestions</div>
                      <div className="company-ai-message-caption">
                        Keep Match confirms the project the email most likely belongs to. Dismissing a match teaches the reviewer memory not to favor it next time.
                      </div>
                      {visibleProjectSuggestions.length ? (
                        <div className="company-ai-citation-list">
                          {visibleProjectSuggestions.map((suggestion) => (
                            <div key={suggestion.emailProjectSuggestionId} className="company-ai-citation">
                              <div className="company-ai-citation-title">
                                {suggestion.title || `Project ${suggestion.projectId || 'unknown'}`}
                                {' · '}
                                {Math.round(suggestion.confidence * 100)}%
                              </div>
                              <div className="company-ai-citation-copy">
                                {formatReasonCodeList(suggestion.reasonCodes || []) || 'No ranking reasons recorded'}
                              </div>
                              {suggestion.graphEvidence?.length ? (
                                <div className="company-ai-graph-evidence">
                                  <div className="company-ai-graph-evidence-chips">
                                    {suggestion.graphEvidence.slice(0, 4).map((evidence, index) => (
                                      <span key={`${evidence.relationPath}-${index}`} className="company-ai-graph-evidence-chip">
                                        {evidence.label}
                                      </span>
                                    ))}
                                  </div>
                                  <details className="company-ai-graph-evidence-details">
                                    <summary>Graph evidence</summary>
                                    <div className="company-ai-graph-evidence-detail-list">
                                      {suggestion.graphEvidence.slice(0, 4).map((evidence, index) => (
                                        <div key={`${evidence.relationPath}-detail-${index}`}>
                                          {formatGraphEvidenceDetail(evidence)}
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </div>
                              ) : null}
                              {suggestion.evidence.length ? (
                                <div className="company-ai-citation-copy">
                                  Evidence: {formatEvidenceSummary(suggestion.evidence)}
                                </div>
                              ) : null}
                              <div className="company-ai-form-actions">
                                <Button variant="secondary" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('project', suggestion.emailProjectSuggestionId, 'accepted')}>Keep Match</Button>
                                <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('project', suggestion.emailProjectSuggestionId, 'rejected')}>Dismiss</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="company-ai-muted">No project suggestions were generated.</div>
                      )}
                    </div>

                    <div className="company-ai-result-box company-ai-result-box--compact">
                      <div className="company-ai-section-title company-ai-section-title--flush">Task suggestions</div>
                      <div className="company-ai-message-caption">
                        Task suggestions are built from explicit request phrases in the email body. Keeping a suggestion does not create a PM task yet; it only makes the Create Task step available when the suggestion is eligible.
                      </div>
                      {selectedEmailReviewDetail.taskSuggestions.length ? (
                        <div className="company-ai-citation-list">
                          {selectedEmailReviewDetail.taskSuggestions.map((suggestion) => (
                            <div key={suggestion.emailTaskSuggestionId} className="company-ai-citation company-ai-citation--task">
                              <div className="company-ai-citation-title company-ai-citation-title--with-status">
                                <span>{suggestion.suggestedTitle}</span>
                                <EmailReviewStatusBadge value={suggestion.createdTaskId ? 'accepted' : suggestion.reviewStatus} />
                              </div>
                              <div className="company-ai-citation-copy">
                                {formatReasonCodeList(suggestion.reasonCodes || []) || 'No extraction reasons recorded'}
                                {suggestion.suggestedDueDate ? ` · due ${suggestion.suggestedDueDate}` : ''}
                              </div>
                              {suggestion.evidence.length ? (
                                <div className="company-ai-citation-copy">
                                  Triggered by: {formatEvidenceSummary(suggestion.evidence)}
                                </div>
                              ) : null}
                              <div className="company-ai-citation-copy">
                                {suggestion.createdTaskId
                                  ? `Created task ${suggestion.createdTaskId}${suggestion.createdTaskAt ? ` · ${formatDateTime(suggestion.createdTaskAt)}` : ''}`
                                  : suggestion.canCreateTask
                                    ? 'Approved action can create a PM task'
                                    : suggestion.taskCreationBlockedReason || 'Review-only suggestion'}
                              </div>
                              <div className="company-ai-form-actions company-ai-form-actions--approval">
                                <Button loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('task', suggestion.emailTaskSuggestionId, 'accepted')}>Approve Action Draft</Button>
                                <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('task', suggestion.emailTaskSuggestionId, 'rejected')}>Dismiss</Button>
                                {suggestion.reviewStatus === 'accepted' && suggestion.canCreateTask && !suggestion.createdTaskId ? (
                                  <Button
                                    loading={emailReviewLoading}
                                    onClick={() => handleOpenTaskCreateForm(suggestion.emailTaskSuggestionId)}
                                  >
                                    Create Task
                                  </Button>
                                ) : null}
                              </div>
                              {openTaskCreateSuggestionId === suggestion.emailTaskSuggestionId ? (
                                <div className="company-ai-stacked">
                                  <Input
                                    label="Project"
                                    value={
                                      selectedEmailReviewDetail.projectSuggestions.find((projectSuggestion) => projectSuggestion.projectId === suggestion.suggestedProjectId)?.title
                                      || (suggestion.suggestedProjectId ? `Project ${suggestion.suggestedProjectId}` : 'No matched project')
                                    }
                                    readOnly
                                    fullWidth
                                  />
                                  <Input
                                    label="Task title"
                                    value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.title || ''}
                                    onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'title', event.target.value)}
                                    fullWidth
                                  />
                                  <Textarea
                                    label="Task description"
                                    value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.description || ''}
                                    onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'description', event.target.value)}
                                    rows={3}
                                    fullWidth
                                  />
                                  <div className="company-ai-form-grid company-ai-form-grid--quota-compact">
                                    <Input
                                      label="Due date"
                                      type="date"
                                      value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.dueDate || ''}
                                      onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'dueDate', event.target.value)}
                                      fullWidth
                                    />
                                    <Select
                                      label="Priority"
                                      value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.priority || ''}
                                      onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'priority', event.target.value)}
                                      options={[
                                        { value: '', label: 'Use suggestion/default' },
                                        { value: 'low', label: 'Low' },
                                        { value: 'medium', label: 'Medium' },
                                        { value: 'high', label: 'High' },
                                      ]}
                                      fullWidth
                                    />
                                  </div>
                                  <div className="company-ai-form-actions">
                                    <Button loading={emailReviewLoading} onClick={() => void handleCreateTaskFromSuggestion(suggestion.emailTaskSuggestionId)}>
                                      Confirm Create Task
                                    </Button>
                                    <Button variant="ghost" loading={emailReviewLoading} onClick={() => setOpenTaskCreateSuggestionId(null)}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="company-ai-muted">No task suggestions were generated.</div>
                      )}
                    </div>

                    <div className="company-ai-result-box company-ai-result-box--compact">
                      <div className="company-ai-section-title company-ai-section-title--flush">Attachment suggestions</div>
                      <div className="company-ai-message-caption">
                        Attachment suggestions are filing hints only. Keeping one records the preferred project folder/category for review, but it does not move, upload, or rename the file yet.
                      </div>
                      {selectedEmailReviewDetail.fileSuggestions.length ? (
                        <div className="company-ai-citation-list">
                          {selectedEmailReviewDetail.fileSuggestions.map((suggestion) => (
                            <div key={suggestion.emailFileSuggestionId} className="company-ai-citation">
                              <div className="company-ai-citation-title">{suggestion.attachmentName}</div>
                              <div className="company-ai-citation-copy">
                                {suggestion.suggestedCategory || 'other'}
                                {suggestion.suggestedFolder ? ` · ${suggestion.suggestedFolder}` : ''}
                              </div>
                              {suggestion.suggestedQuotationNumber ? (
                                <div className="company-ai-citation-copy">Quotation {suggestion.suggestedQuotationNumber}</div>
                              ) : null}
                              {suggestion.suggestedFileName ? (
                                <div className="company-ai-citation-copy"><strong>File as:</strong> {suggestion.suggestedFileName}</div>
                              ) : null}
                              <div className="company-ai-citation-copy">
                                {formatReasonCodeList(suggestion.reasonCodes || []) || 'No filing reasons recorded'}
                              </div>
                              <div className="company-ai-form-actions">
                                <Button variant="secondary" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('file', suggestion.emailFileSuggestionId, 'accepted')}>Keep Filing Hint</Button>
                                <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('file', suggestion.emailFileSuggestionId, 'rejected')}>Dismiss</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="company-ai-muted">No attachment suggestions were generated.</div>
                      )}
                      </div>
                    </aside>
                  </>
                ) : (
                  <EmptyState
                    icon="🔎"
                    title="Select an email"
                    description="Choose a recent ingested email to inspect the suggestion-only review detail."
                    className="company-ai-admin-empty-state"
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
        </>}
      </PageSection>
    </div>
  );
}
